import { VehicleRepository, VehicleStatusListener, VehicleStatusErrorListener, VehiclePostCommandError, RTCRequestListener, RTCResponseListener } from 'core/repositories/VehicleRepository';
import { VehicleRepository as FallbackRepository } from 'infrastructure/dummy/VehicleRepository';
import { Vehicle } from 'core/models/vehicle';
import { VehicleRegistryApiCall, VehicleListResponse, VehicleArrayResponse, isVehicleArrayResponse, VehicleResponse } from './api/VehicleRegistry';
import { DriveCommand } from 'core/models/drive';
import { MediaDeviceApiCall, MediaDeviceListResponse } from './api/MediaDevice';
import { Project } from 'core/models/project';
import { ControlApiCall } from './api/Control';
import { VehicleStatusPublisher } from './VehicleStatusPublisher';
import { StreamConnector } from './api/StreamConnector';
import { VehicleStatusSubscriber } from './VehicleStatusSubscriber';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { StableWebSocket } from 'infrastructure/utility/StableWebSocket';
import { VehicleStandByConnection } from './VehicleStandByConnection';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { ignore } from 'utility/ignore';
import { makeCameraDevice, makeMicrophoneDevice } from 'core/models/mediaDevice';
import { Environment } from 'core/models/environment';
import { RTCEventListener, VehicleRTCStatusSubscriber } from './VehicleRTCStatusSubscriber';
import { RTCTask } from 'core/models/rtc';

class VehicleRepositoryImpl implements VehicleRepository {
    readonly ws: StableWebSocket;
    private readonly _fallback = new FallbackRepository();
    private readonly callApi: VehicleRegistryApiCall;
    private readonly deviceApiCall: MediaDeviceApiCall;
    private readonly statusRepos: VehicleStatusPublisher;
    private readonly statusStream: VehicleStatusSubscriber;
    private readonly standbyConnection: VehicleStandByConnection;
    private readonly rtcStream: VehicleRTCStatusSubscriber;

    constructor(callApi: VehicleRegistryApiCall, deviceApiCall: MediaDeviceApiCall, private readonly controlApiCall: ControlApiCall, connectStream: StreamConnector) {
        this.callApi = callApi;
        this.deviceApiCall = deviceApiCall;

        const ws = new StableWebSocket(() => connectStream('/v1/control/connect'));
        this.ws = ws;

        this.statusStream = new VehicleStatusSubscriber(ws, error => this.notifyError(error));
        this.statusRepos = new VehicleStatusPublisher(controlApiCall, ws, error => this.notifyError(error));
        this.standbyConnection = new VehicleStandByConnection(ws);
        this.rtcStream = new VehicleRTCStatusSubscriber(ws, error => this.notifyError(error));

        ws.connect(); // intentionally non-awaited
    }

    async getVehicles(projectId: Project.Id, environmentId: Environment.Id): Promise<Vehicle[]> {
        const json = await this.callApi<VehicleListResponse | VehicleArrayResponse>(`/v1/projects/${projectId}/environments/${environmentId}/vehicles`);

        const rs =
            isVehicleArrayResponse(json)
                ? json.map(async v => {
                    const devices = await this.getMediaDevices(v.vehicle_id, projectId, environmentId);
                    const cameras = devices.filter(dev => dev.type == 'camera' || dev.type == '').map(makeCameraDevice);
                    const microphones = devices.filter(dev => dev.type == 'microphone').map(makeMicrophoneDevice);
                    return { id: v.vehicle_id, 'project_id': v.project_id, name: v.vehicle_name, cameras, microphones };
                })
                : json.vehicles.map(async v => {
                    const x = await this._fallback.getDummyVehicle();
                    return { ...x, ...v };
                });
        const vs = await Promise.all(rs);

        return vs;
    }

    async getVehicle(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id): Promise<Vehicle> {
        const json = await this.callApi<VehicleResponse>(`/v1/projects/${projectId}/environments/${environmentId}/vehicles/${vehicleId}`);
        const { id, name } = 'vehicle' in json
            ? json.vehicle
            : {
                id: json.vehicle_id,
                name: json.vehicle_name,
            };

        const devices = await this.getMediaDevices(vehicleId, projectId, environmentId);
        const cameras = devices.filter(dev => dev.type == 'camera' || dev.type == '').map(makeCameraDevice);
        const microphones = devices.filter(dev => dev.type == 'microphone').map(makeMicrophoneDevice);

        return { id, name, cameras, microphones };
    }

    private async getMediaDevices(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id) {
        const json = await this.deviceApiCall<MediaDeviceListResponse>(`/v1/projects/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/devices`);
        return json.devices.map(d => ({ ...d, tag: (d.tag && d.tag !== '') ? d.tag : d.position }));
    }

    async postDriveCommand(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, commandDelta: Partial<DriveCommand>): Promise<void> {
        return this.statusRepos.postDriveCommand(vehicleId, projectId, environmentId, commandDelta);
    }

    async cancelPostDriveCommand(vehicleId: Vehicle.Id): Promise<void> {
        return this.statusRepos.cancelPostDriveCommand(vehicleId);
    }

    postHeartbeat(vehicleId: Vehicle.Id, projectId: Project.Id) {
        this.standbyConnection.post(vehicleId, projectId);
    }

    private readonly vehicleHeartbeatListenerManager = createKeyedListenerManager<Vehicle.Id, (timestamp: number, accept: boolean) => void>((vehicleId, listeners) => {
        const listener = (timestamp: number, accept: boolean) => {
            [...listeners].forEach(listener => listener(timestamp, accept));
        };
        this.standbyConnection.addAckListener(vehicleId, listener);
        return () => {
            this.standbyConnection.removeAckListener(vehicleId, listener);
        };
    });

    addVehicleHeartbeatListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: (timestamp: number, accept: boolean) => void) {
        ignore(projectId);
        this.vehicleHeartbeatListenerManager.addListener(vehicleId, listener);
    }

    removeVehicleHeartbeatListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: (timestamp: number, accept: boolean) => void) {
        ignore(projectId);
        this.vehicleHeartbeatListenerManager.removeListener(vehicleId, listener);
    }

    private readonly latestVehicleListenerMap = new Map<VehicleStatusListener, VehicleStatusListener>();

    addVehicleStatusListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: VehicleStatusListener): void {
        const wrapping = (status: Vehicle.Status) => {
            this.statusRepos.setLatestStatus(vehicleId, status);
            listener(status);
        };
        this.latestVehicleListenerMap.set(listener, wrapping);

        this.statusStream.addVehicleStatusListener(vehicleId, projectId, wrapping);
    }

    removeVehicleStatusListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: VehicleStatusListener): void {
        const wrapping = this.latestVehicleListenerMap.get(listener);
        if (!wrapping) return;

        this.statusStream.removeVehicleStatusListener(vehicleId, projectId, wrapping);
    }

    private readonly errorListenerManager = createListenerManager<VehicleStatusErrorListener>();

    addVehicleStatusErrorListener(listener: VehicleStatusErrorListener) {
        this.errorListenerManager.addListener(listener);
    }

    removeVehicleStatusErrorListener(listener: VehicleStatusErrorListener) {
        this.errorListenerManager.addListener(listener);
    }

    private notifyError(error: unknown) {
        [...this.errorListenerManager.listeners].forEach(listener => {
            listener(new VehiclePostCommandError(error));
        });
    }

    private rtcListenerAdaptors = new Map<RTCRequestListener | RTCResponseListener, RTCEventListener>();

    addRTCRequestListener(vehicleId: string, projectId: string, listener: RTCRequestListener): void {
        const wrapped: RTCEventListener = (event, arg) => {
            if (event === 'task') listener(arg as RTCTask[]);
        };
        this.rtcListenerAdaptors.set(listener, wrapped);
        this.rtcStream.addListener(vehicleId, projectId, wrapped);
    }

    removeRTCRequestListener(vehicleId: string, projectId: string, listener: RTCRequestListener): void {
        const wrapped = this.rtcListenerAdaptors.get(listener);
        if (wrapped) {
            this.rtcStream.removeListener(vehicleId, projectId, wrapped);
            this.rtcListenerAdaptors.delete(listener);
        }
    }

    addRTCCommandResponseListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: RTCResponseListener) {
        const wrapped = (event: string, ...args: unknown[]) => {
            if (event === 'response') listener(...(args as [{ success: boolean; uuid: string; }[], string | undefined]));
        };
        this.rtcListenerAdaptors.set(listener, wrapped);
        this.rtcStream.addListener(vehicleId, projectId, wrapped);
    }

    removeRTCCommandResponseListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: RTCResponseListener) {
        const wrapped = this.rtcListenerAdaptors.get(listener);
        if (wrapped) {
            this.rtcStream.removeListener(vehicleId, projectId, wrapped);
            this.rtcListenerAdaptors.delete(listener);
        }
    }

    async postRTCCommand(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, task: RTCTask, activated: boolean, transactionId: string): Promise<void> {
        this.controlApiCall(`/v1/projects/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/rtc_commands_poc`, {
            method: 'POST',
            body: JSON.stringify({
                commands: [{
                    uuid: task.id,
                    module: task.taskType,
                    command: activated ? 'ACTIVATE' : 'DEACTIVATE',
                }],
                transaction_id: transactionId,
            }),
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' }
        });
    }

    getWebSocket(): StableWebSocket | undefined {
        return this.ws;
    }
}

export { VehicleRepositoryImpl as VehicleRepository };
